/**
 * LotSmith — Generic empty / loading / error state components.
 *
 * Consistent visual grammar across the dashboard:
 *   - EmptyState  → neutral, "nothing yet, here's what to do"
 *   - LoadingState→ skeleton with pulsing bg tokens
 *   - ErrorState  → danger-toned, action to retry
 *
 * All three accept an optional `cta` prop that renders a primary button.
 */
import type { ReactNode } from "react";

export interface StateProps {
  title: string;
  body?: string;
  cta?: { label: string; onClick: () => void } | undefined;
  icon?: ReactNode;
}

export function EmptyState({ title, body, cta, icon }: StateProps) {
  return (
    <div className="state state--empty">
      {icon && <div className="state__icon">{icon}</div>}
      <div className="state__title">{title}</div>
      {body && <div className="state__body">{body}</div>}
      {cta && (
        <button type="button" className="btn btn--primary state__cta" onClick={cta.onClick}>
          {cta.label}
        </button>
      )}
    </div>
  );
}

export function ErrorState({ title, body, cta }: StateProps) {
  return (
    <div className="state state--error">
      <div className="state__icon" aria-hidden>⚠</div>
      <div className="state__title">{title}</div>
      {body && <div className="state__body">{body}</div>}
      {cta && (
        <button type="button" className="btn btn--primary state__cta" onClick={cta.onClick}>
          {cta.label}
        </button>
      )}
    </div>
  );
}

export interface SkeletonProps {
  /** CSS height shorthand — e.g. "200px", "100%". */
  height?: string;
  /** Number of stacked skeleton lines. */
  lines?: number;
}

export function Skeleton({ height = "16px", lines = 1 }: SkeletonProps) {
  if (lines === 1) return <div className="skeleton" style={{ height }} />;
  return (
    <div className="skeleton-stack">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="skeleton" style={{ height, width: i === lines - 1 ? "60%" : "100%" }} />
      ))}
    </div>
  );
}

export function LoadingMetricCard({ label }: { label: string }) {
  return (
    <div className="metric metric--loading">
      <div className="metric__label">{label}</div>
      <div className="skeleton skeleton--metric" />
      <div className="skeleton skeleton--meta" />
    </div>
  );
}
